import { logOpenDispenseIntervalSection } from '@/custom-snowplow-events';
import { datadogRum } from '@datadog/browser-rum';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import { Stack, Typography } from '@montugroup/design-system';
import { useEffect, useRef } from 'react';
import { DispenseIntervalAlert } from './DispenseIntervalAlert';
import type { OkToDispenseOrderProduct } from './unable-to-dispense.types';

function DispenseIntervalSectionHeading() {
  return (
    <Stack gap={2}>
      <Typography variant="caption" letterSpacing={'1px'} color="textSecondary">
        Interval time
      </Typography>
      <Typography variant="body1">
        The patient’s dispensing history{' '}
        <Typography variant="body1" fontWeight={600} color="success" display={'inline'}>
          does not indicate interval time
        </Typography>
        .
      </Typography>
    </Stack>
  );
}

const client = getTsPharmacyClient();

export function DispenseIntervalSection({ orderCode, productIds }: { orderCode: string; productIds: number[] }) {
  const { data, isLoading, isError } = client.getOrderDispenseIntervals.useQuery({
    queryKey: [...client.getOrderDispenseIntervals.keys, orderCode],
    queryData: {
      params: {
        orderCode,
      },
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orderProducts = data?.body.data.orderProducts ?? [];

  // Render an alert for each eligible product; products that are not 'ok' are omitted (the
  // "indicates interval time" state is descoped for this release). If no flagged product is
  // eligible, we render nothing.
  const okToDispenseProducts = productIds
    .map((productId) => orderProducts.find((orderProduct) => orderProduct.product.id === productId))
    .filter((orderProduct): orderProduct is OkToDispenseOrderProduct => orderProduct?.eligibility === 'ok');

  const hasLogged = useRef(false);

  useEffect(() => {
    if (!isLoading && !isError && okToDispenseProducts.length > 0 && !hasLogged.current) {
      const data = {
        orderCode,
        products: orderProducts.map((orderProduct) => {
          let currentInterval;
          if (orderProduct.eligibility === 'ok' || orderProduct.eligibility === 'interval') {
            const { dispenses, interval, maxUnits } = orderProduct.currentWindow;
            currentInterval = {
              interval,
              // sum all of the dispensed units
              dispensedUnits: dispenses.reduce((acc, { units }) => acc + units, 0),
              maxUnits,
            };
          }

          return {
            name: orderProduct.product.name,
            eligibility: orderProduct.eligibility,
            currentInterval,
          };
        }),
      };

      datadogRum.addAction('interval-time-section-rendered', data);
      logOpenDispenseIntervalSection(data);
      hasLogged.current = true;
    }
  }, [isLoading, isError, okToDispenseProducts, orderCode, orderProducts]);

  if (okToDispenseProducts.length === 0 || isLoading || isError) {
    return null;
  }

  return (
    <Stack gap={2}>
      <DispenseIntervalSectionHeading />
      {okToDispenseProducts.map((orderProduct) => (
        <DispenseIntervalAlert key={orderProduct.product.id} orderProduct={orderProduct} />
      ))}
    </Stack>
  );
}
